import { useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import { ApiErrorState } from '@/components/ApiErrorState';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { PromotionRedemptionView, PromotionView } from '@/api/generated';
import { useTenantApi } from '@/tenant/useTenant';
import { formatInstantOrDash } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { Icon, resolveTokens } from '@/theme';

export interface RedemptionsDrawerProps {
  open: boolean;
  promotion: PromotionView | null;
  onClose: () => void;
}

export function RedemptionsDrawer({ open, promotion, onClose }: RedemptionsDrawerProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const api = useTenantApi();
  const [rows, setRows] = useState<PromotionRedemptionView[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  const titleId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const id = promotion?.id;

  useEffect(() => {
    if (!open || id === undefined) {
      setRows(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setRows(null);
    setError(null);
    api.listRedemptions(id).then(
      (list) => {
        if (!cancelled) {
          setError(null);
          setRows(list.redemptions ?? []);
        }
      },
      (err: unknown) => {
        if (!cancelled) {
          setRows(null);
          setError(err);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [api, open, id, attempt]);

  return (
    // The Drawer's paper is the modal region (role="dialog"): it is named by the title (A11Y-006).
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: { 'aria-labelledby': titleId },
        // Once the drawer has slid in (its focus trap has focused the paper), focus moves to the
        // heading; on close, MUI's restore returns it to the control that opened it (TMMRSL-0073).
        transition: { onEntered: () => heading.current?.focus() },
      }}
    >
      <Box sx={{ width: { xs: 1, sm: 400 }, p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" component="p" sx={{ color: tokens.text2 }}>
              {t('promotions.redemptions.title')}
            </Typography>
            <Typography
              id={titleId}
              ref={heading}
              tabIndex={-1}
              variant="h6"
              component="h2"
              sx={{ '&:focus:not(:focus-visible)': { outline: 'none' } }}
            >
              {promotion?.code ?? t('promotions.unavailable')}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label={t('common.close')} sx={{ mt: -0.5 }}>
            <Icon name="close" size="md" />
          </IconButton>
        </Box>
        {error != null ? (
          <ApiErrorState error={error} onRetry={() => setAttempt((n) => n + 1)} />
        ) : rows === null ? (
          <Box role="status" aria-label={t('common.loading')}>
            <Skeleton variant="rounded" height={72} sx={{ mb: 1 }} />
            <Skeleton variant="rounded" height={72} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography>{t('promotions.redemptions.empty')}</Typography>
        ) : (
          <List disablePadding>
            {rows.map((row) => (
              <ListItem
                key={row.id ?? `${row.redeemedAt}-${row.redeemedBy}`}
                alignItems="flex-start"
                disableGutters
              >
                <ListItemText
                  primary={row.redeemedBy ?? t('promotions.unavailable')}
                  secondary={t('promotions.redemptions.line', {
                    redeemedAt: t('promotions.redemptions.redeemedAt'),
                    redeemedAtValue: formatInstantOrDash(
                      row.redeemedAt,
                      locale,
                      t('promotions.unavailable'),
                    ),
                    remaining: t('promotions.redemptions.remaining'),
                    remainingValue: row.statementsRemaining ?? t('promotions.unavailable'),
                    endedAt: t('promotions.redemptions.endedAt'),
                    endedAtValue: formatInstantOrDash(row.endedAt, locale, t('promotions.unavailable')),
                  })}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Drawer>
  );
}
